import { FC, ReactNode, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Sheet } from '../../lib/Types'
import Paywall from '../Paywall'
import { SheetType } from './lib/Enums/SheetType'
import { isPaymentOverdue } from './lib/Payment'
import TrialEndedPaywallContent from './TrialEndedPaywallContent'

interface Props {
  sheet: Sheet
  children: (guard: (action: () => void) => () => void) => ReactNode
}

/**
 * Wraps a set of sheet actions and shows a "trial ended" paywall instead of
 * running the action whenever the sheet's payment is overdue. Use the
 * `guard` callback to wrap individual onClick handlers, e.g.
 * `onClick={guard(() => doTheThing())}`.
 */
const PaymentGuard: FC<Props> = ({ sheet, children }) => {
  const { t } = useTranslation('common')
  const [showPaywall, togglePaywall] = useState(false)
  const genericName =
    sheet.sheet_type === SheetType.BUDGET
      ? t('common:paywall.budget')
      : sheet.sheet_type === SheetType.SPREADSHEET
        ? t('common:paywall.spreadsheet')
        : t('common:paywall.accounting')

  const guard = (action: () => void) => () => {
    if (isPaymentOverdue(sheet)) {
      togglePaywall(true)
      return
    }

    action()
  }

  return (
    <>
      {children(guard)}
      <Paywall
        show={showPaywall}
        onHide={() => togglePaywall(false)}
        sheetType={sheet.sheet_type}
        sheetId={sheet.id}
        name={sheet.name || genericName}
      >
        <TrialEndedPaywallContent sheet={sheet} />
      </Paywall>
    </>
  )
}

export default PaymentGuard
